import { useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_ANGLE } from "@shared/outreachAngle";
import { useRoute, Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Send, Paperclip, X, CheckCircle2, Circle, AlertTriangle,
  Loader2, FlaskConical, Users, Search, Mail, Sparkles, ClipboardCheck, RotateCcw, CheckCheck,
} from "lucide-react";
import { DraftReviewDialog } from "@/components/campaigns/DraftReviewDialog";
import { useOutreachLists } from "@/hooks/useOutreach";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useUpload } from "@/hooks/use-upload";
import {
  useBlastCampaign, useBlastMailboxes, useBlastMutations, useComposeMeta,
  type RecipientReport, type BlastRecipient,
} from "@/hooks/useBlast";

interface ClientOption { id: string; name: string }

// The body composer is plain text: escape everything the user types so a
// literal "<" stays a "<" in the email — and so nothing in the live preview
// (which renders raw HTML) can execute. Merge values are escaped separately
// because contact names/companies come from imported external data.
const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const textToHtml = (t: string) =>
  t.split(/\n{2,}/).map((par) => par.trim()).filter(Boolean)
    .map((par) => `<p style="margin:0 0 8px 0">${escapeHtml(par).replace(/\n/g, "<br>")}</p>`).join("");
const htmlToText = (h: string) =>
  h.replace(/<p[^>]*>(&nbsp;)?<\/p>/g, "\n").replace(/<p[^>]*>/g, "").replace(/<\/p>/g, "\n\n").replace(/<br\s*\/?>/g, "\n").replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&amp;/g, "&").replace(/\n$/, "");

// The firm-standard angle used to pre-draft a market outreach campaign the
// moment the type is selected. Fully editable; positioning per Caleb.
const US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
];
const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"]; // index 0=Sun..6=Sat
const HOURS = Array.from({ length: 25 }, (_, h) => h);

function hourLabel(h: number): string {
  if (h === 0) return "12:00 AM";
  if (h === 24) return "12:00 AM (+1d)";
  if (h === 12) return "12:00 PM";
  return h < 12 ? `${h}:00 AM` : `${h - 12}:00 PM`;
}

function tzAbbrev(tz: string): string {
  return US_TIMEZONES.find((t) => t.value === tz)?.label.match(/\(([^)]+)\)/)?.[1] ?? "CT";
}

// UTC ISO -> "YYYY-MM-DDTHH:mm" wall-clock in tz, for a datetime-local input.
function utcToLocalInput(utcIso: string, tz: string): string {
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date(utcIso));
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  let hh = g("hour"); if (hh === "24") hh = "00";
  return `${g("year")}-${g("month")}-${g("day")}T${hh}:${g("minute")}`;
}

function SectionCard({ done, title, children }: { done: boolean; title: string; children: React.ReactNode }) {
  return (
    <section className="bg-card border border-border rounded-lg p-4">
      <h2 className="flex items-center gap-2 text-sm font-semibold mb-3">
        {done
          ? <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
          : <Circle className="w-4 h-4 text-muted-foreground/40" />}
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function CampaignComposer() {
  const [isEdit, editParams] = useRoute("/campaigns/:id/edit");
  const [, setLocation] = useLocation();
  const editId = isEdit ? editParams?.id ?? null : null;

  const { data: existing } = useBlastCampaign(editId);
  const { data: meta } = useComposeMeta();
  const { data: mailboxes = [] } = useBlastMailboxes();
  const { data: clients = [] } = useQuery<ClientOption[]>({
    queryKey: ["blast", "client-options"],
    queryFn: async () => {
      const res = await fetch("/api/clients", { credentials: "include" });
      const body = await res.json();
      return (Array.isArray(body) ? body : body.data ?? []).map((c: any) => ({ id: c.id, name: c.name }));
    },
  });
  const m = useBlastMutations();
  const { uploadFile, isUploading } = useUpload({
    onError: (e) => toast.error(`Upload failed: ${e.message}`),
  });

  // ── Form state ──
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [name, setName] = useState("");
  // CIM sends live on the tracker's one-click package flow, not campaigns
  const [type, setType] = useState<"teaser" | "custom" | "market_outreach">("teaser");
  const [clientId, setClientId] = useState("");
  const [personalization, setPersonalization] = useState<"template" | "personalized">("template");
  const [aiAngle, setAiAngle] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  // Recipient source for market outreach: "prospects" or an outreach list id.
  // A ?list=<id> deep link (from the Mass Outreach page) preselects the list.
  const [recipientSource, setRecipientSource] = useState<string>(() => {
    const listParam = new URLSearchParams(window.location.search).get("list");
    return listParam || "prospects";
  });
  const [excludeContacted, setExcludeContacted] = useState(true);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("list")) {
      setType("market_outreach");
      setPersonalization("personalized");
    }
  }, []);
  const { data: outreachLists = [] } = useOutreachLists();

  // Once a list is selected for a Market Outreach campaign, scan it for
  // context and auto-draft the starting point (angle, name, subject,
  // fallback) into empty fields — everything editable.
  const autoDrafted = useRef(false);
  useEffect(() => {
    if (type !== "market_outreach" || personalization !== "personalized") return;
    if (recipientSource === "prospects") return; // the scan needs a list
    if (autoDrafted.current || hydrated.current) return; // never clobber a loaded draft
    if (subject.trim() || body.trim()) return;
    autoDrafted.current = true;
    const angle = aiAngle.trim() || DEFAULT_ANGLE;
    if (!aiAngle.trim()) setAiAngle(angle);
    const progress = toast.loading("Reading the list and drafting a starting point…");
    m.suggestFallback.mutate(
      { angle, listId: recipientSource },
      {
        onSuccess: (s) => {
          setSubject((prev) => prev.trim() ? prev : s.subject);
          setBody((prev) => prev.trim() ? prev : s.body);
          setName((prev) => prev.trim() ? prev : s.name);
          toast.success("Starting point drafted from the list — edit anything you like", { id: progress });
        },
        onError: (e: any) => { autoDrafted.current = false; toast.error(String(e?.message ?? "Drafting failed"), { id: progress }); },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, personalization, recipientSource]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<{ objectPath: string; filename: string; size: number }[]>([]);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [windowStart, setWindowStart] = useState(8);
  const [windowEnd, setWindowEnd] = useState(18);
  const [sendDays, setSendDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [timezone, setTimezone] = useState("America/Chicago");
  const [pacing, setPacing] = useState<"steady" | "fast">("steady");
  const [scheduleMode, setScheduleMode] = useState<"now" | "scheduled">("now");
  const [scheduledStartLocal, setScheduledStartLocal] = useState("");
  const [report, setReport] = useState<RecipientReport | null>(null);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const hydrated = useRef(false);

  // Hydrate when editing an existing draft.
  useEffect(() => {
    if (!existing || hydrated.current) return;
    if (existing.status !== "draft") { setLocation(`/campaigns/${existing.id}`); return; }
    hydrated.current = true;
    setCampaignId(existing.id);
    setName(existing.name);
    setType(existing.campaign_type === "custom" ? "custom" : existing.campaign_type === "market_outreach" ? "market_outreach" : "teaser");
    setClientId(existing.client_id ?? "");
    setPersonalization(existing.personalization === "personalized" ? "personalized" : "template");
    setAiAngle(existing.ai_angle ?? "");
    setSubject(existing.subject);
    setBody(htmlToText(existing.body_html));
    setAttachments((existing.attachments as any) ?? []);
    setCc(((existing as any).cc_addresses ?? []).join(", "));
    setBcc(((existing as any).bcc_addresses ?? []).join(", "));
    setWindowStart(existing.send_window_start);
    setWindowEnd(existing.send_window_end);
    setSendDays(existing.send_days && existing.send_days.length ? existing.send_days : [1, 2, 3, 4, 5]);
    const tz = existing.timezone || "America/Chicago";
    setTimezone(tz);
    setPacing(existing.pacing === "fast" ? "fast" : "steady");
    if (existing.scheduled_start_at) {
      setScheduleMode("scheduled");
      setScheduledStartLocal(utcToLocalInput(existing.scheduled_start_at, tz));
    }
  }, [existing, setLocation]);

  const recipients: BlastRecipient[] = existing?.recipients?.filter((r) => r.status === "pending") ?? [];

  const splitEmails = (s: string) => s.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
  const payload = () => ({
    name, campaignType: type, clientId: type === "market_outreach" ? null : clientId || null, subject,
    bodyHtml: textToHtml(body),
    sendWindowStart: windowStart, sendWindowEnd: windowEnd, attachments,
    ccAddresses: splitEmails(cc), bccAddresses: splitEmails(bcc),
    // Sending mailboxes are managed on the backend; empty = rotate across all
    mailboxIds: [],
    sendDays, timezone, pacing,
    scheduledStartLocal: scheduleMode === "scheduled" && scheduledStartLocal ? scheduledStartLocal : null,
    aiAngle: type === "market_outreach" ? aiAngle : null,
    personalization: type === "market_outreach" ? personalization : "template",
  });

  // ── Autosave (debounced) once the essentials exist ──
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const canPersist = name.trim() && subject.trim() && body.trim() && (type === "custom" || type === "market_outreach" || clientId);
  useEffect(() => {
    if (!canPersist) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        setSaveState("saving");
        if (campaignId) await m.updateCampaign.mutateAsync({ id: campaignId, ...payload() });
        else {
          const created = await m.createCampaign.mutateAsync(payload());
          setCampaignId(created.id);
          window.history.replaceState(null, "", `/campaigns/${created.id}/edit`);
        }
        setSaveState("saved");
      } catch { setSaveState("idle"); }
    }, 1500);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, type, clientId, subject, body, attachments, cc, bcc, windowStart, windowEnd, sendDays, timezone, pacing, scheduleMode, scheduledStartLocal, aiAngle, personalization]);

  const ensureSaved = async (): Promise<string> => {
    if (campaignId) { await m.updateCampaign.mutateAsync({ id: campaignId, ...payload() }); return campaignId; }
    const created = await m.createCampaign.mutateAsync(payload());
    setCampaignId(created.id);
    return created.id;
  };

  // ── Actions ──
  const buildRecipients = async () => {
    if (type !== "market_outreach" && !clientId) { toast.error("Pick a client first — recipients come from its buyer tracker"); return; }
    try {
      const id = await ensureSaved();
      const body = type !== "market_outreach"
        ? { id, source: "tracker" }
        : recipientSource === "prospects"
          ? { id, source: "prospects" }
          : { id, source: "outreach_list", listId: recipientSource, excludeContacted };
      const r = await m.buildRecipients.mutateAsync(body as any);
      setReport(r);
      toast.success(`${r.added} recipients ready`);
    } catch (e: any) { toast.error(e.message); }
  };

  // mode "missing" only fills recipients with no draft (default, never
  // clobbers what you've already read); "all" wipes and redoes everything.
  const generateAllDrafts = async (mode: "missing" | "all" = "missing") => {
    if (!aiAngle.trim()) { toast.error("Write the angle first — it drives every draft"); return; }
    try {
      const id = await ensureSaved();
      if (mode === "all") {
        const approved = recipients.filter((r) => r.draft_status === "approved").length;
        const warning = approved > 0 ? `\n\n${approved} approved draft${approved > 1 ? "s" : ""} will be discarded too.` : "";
        if (!window.confirm(`Start over? Every draft on this campaign will be deleted and rewritten from the current angle.${warning}`)) return;
        await m.clearDrafts.mutateAsync(id);
      }
      // Chunked so big lists never blow a single-request timeout; each chunk
      // is a few Claude calls (~25s).
      const targets = mode === "all"
        ? recipients.map((r) => r.id)
        : recipients.filter((r) => !r.custom_body).map((r) => r.id);
      if (targets.length === 0) { toast.info("Every recipient already has a draft. Use Start over to rewrite them."); return; }
      const CHUNK = 6;
      let generated = 0;
      const failed: string[] = [];
      const progress = toast.loading(`Generating drafts… 0/${targets.length}`);
      for (let i = 0; i < targets.length; i += CHUNK) {
        const r = await m.generateDrafts.mutateAsync({ id, recipientIds: targets.slice(i, i + CHUNK) });
        generated += r.generated;
        failed.push(...r.failed);
        toast.loading(`Generating drafts… ${Math.min(i + CHUNK, targets.length)}/${targets.length}`, { id: progress });
      }
      toast.dismiss(progress);
      if (failed.length) toast.warning(`${generated} drafts generated, ${failed.length} failed — click Generate again to retry just those`);
      else toast.success(`${generated} drafts generated`);
    } catch (e: any) { toast.error(e.message); }
  };

  const handleAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (attachments.reduce((s, a) => s + a.size, 0) + file.size > 3 * 1024 * 1024) {
      toast.error("Attachments are limited to 3 MB total. Send larger files as data-room links.");
      return;
    }
    const res = await uploadFile(file);
    if (res) {
      setAttachments((a) => [...a, { objectPath: res.objectPath, filename: file.name, size: file.size }]);
      toast.success(`Attached ${file.name}`);
    }
    e.target.value = "";
  };

  const testSend = async () => {
    try {
      const id = await ensureSaved();
      const r = await m.testSend.mutateAsync(id);
      toast.success(`Test sent to ${r.sentTo}`);
    } catch (e: any) { toast.error(e.message); }
  };

  const launch = async () => {
    try {
      const id = await ensureSaved();
      await m.lifecycle.mutateAsync({ id, action: "launch" });
      toast.success("Campaign launched");
      setLocation(`/campaigns/${id}`);
    } catch (e: any) { toast.error(e.message); }
  };

  // ── Derived ──
  // Sending mailboxes are backend-managed; campaigns rotate across all of them.
  const readyMailboxes = mailboxes.filter((mb) => mb.isActive && mb.hasToken);
  // Use today's effective cap (respects warmup ramp) so the ETA is realistic.
  const dailyCapacity = readyMailboxes.reduce((s, mb) => s + mb.capToday, 0);
  const anyWarming = readyMailboxes.some((mb) => mb.warmup.active);
  const recipientCount = report?.added ?? recipients.length;
  const estDays = dailyCapacity > 0 && recipientCount > 0 ? Math.ceil(recipientCount / dailyCapacity) : null;
  const previewMailbox = readyMailboxes[0] ?? mailboxes[0] ?? null;
  const sampleRecipient = recipients[0] ?? null;
  const sampleName = sampleRecipient?.name || "Alex Example";
  const sampleCompany = sampleRecipient?.company_name || "Example Capital Partners";

  const mergedPreview = useMemo(() => {
    const firstName = sampleName.split(/\s+/)[0];
    // Subject renders as React text (auto-escaped) → plain values.
    const mergeText = (t: string) => t
      .replace(/\{\{\s*first_name\s*\}\}/gi, firstName)
      .replace(/\{\{\s*full_name\s*\}\}/gi, sampleName)
      .replace(/\{\{\s*company_name\s*\}\}/gi, sampleCompany);
    // Body goes through dangerouslySetInnerHTML → merge values (which come
    // from imported contact data) are HTML-escaped before injection.
    const mergeHtml = (t: string) => t
      .replace(/\{\{\s*first_name\s*\}\}/gi, escapeHtml(firstName))
      .replace(/\{\{\s*full_name\s*\}\}/gi, escapeHtml(sampleName))
      .replace(/\{\{\s*company_name\s*\}\}/gi, escapeHtml(sampleCompany));
    // Signatures embed the logo by CID (unresolvable in a browser preview) —
    // swap in the data URI, and only append the standalone logo for legacy
    // signatures that don't carry their own. Mirrors composeHtml.
    const rawSig = previewMailbox?.signatureHtml ?? "";
    const sigHasLogo = rawSig.includes("cid:ftc-logo");
    const sig = rawSig ? `<br><br>${meta ? rawSig.replace(/cid:ftc-logo/g, meta.logoDataUri) : rawSig}` : "";
    const logo = meta && !sigHasLogo ? `<div style="margin-top:10px"><img src="${meta.logoDataUri}" alt="First Turn Capital" width="180" style="width:180px;height:auto;border:0"></div>` : "";
    return {
      subject: mergeText(subject || "(no subject yet)"),
      html: `${mergeHtml(textToHtml(body || "Start typing your message on the left..."))}${sig}${logo}${meta?.complianceFooterHtml ?? ""}`,
    };
  }, [subject, body, previewMailbox, meta, sampleName, sampleCompany]);

  const isPersonalized = type === "market_outreach" && personalization === "personalized";
  const draftsMissing = isPersonalized ? recipients.filter((r) => !r.custom_body).length : 0;
  const draftsApproved = isPersonalized ? recipients.filter((r) => r.draft_status === "approved").length : 0;
  const reviewRequired = isPersonalized ? Math.min(10, recipients.length) : 0;

  const launchProblems: string[] = [];
  if (!canPersist) launchProblems.push("complete the setup and message sections");
  if (recipientCount === 0) launchProblems.push("build the recipient list");
  if (readyMailboxes.length === 0) launchProblems.push("outreach mailboxes are still being provisioned");
  if (isPersonalized && recipients.length > 0) {
    if (draftsMissing > 0) launchProblems.push(`generate drafts for ${draftsMissing} recipient${draftsMissing > 1 ? "s" : ""}`);
    else if (draftsApproved < reviewRequired) launchProblems.push(`approve at least ${reviewRequired} drafts (${draftsApproved} so far — the rest auto-approve)`);
  }

  const visibleRecipients = recipientSearch
    ? recipients.filter((r) =>
        `${r.name ?? ""} ${r.email} ${r.company_name ?? ""}`.toLowerCase().includes(recipientSearch.toLowerCase()))
    : recipients;

  return (
    <AppLayout title={editId ? "Edit campaign" : "New campaign"}>
      <div className="page-enter pb-24">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="min-w-0">
            <Link href="/campaigns" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-3 h-3" /> Campaigns
            </Link>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Untitled campaign"
              className="mt-1 text-xl font-semibold border-0 border-b rounded-none px-0 h-9 focus-visible:ring-0 focus-visible:border-primary max-w-md"
            />
          </div>
          <span className="text-xs text-muted-foreground shrink-0">
            {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Draft saved" : campaignId ? "Draft" : ""}
          </span>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
          {/* ── Left: form sections ── */}
          <div className="space-y-4">
            <SectionCard done={!!(name.trim() && (type === "custom" || type === "market_outreach" || clientId))} title="Setup">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={type} onValueChange={(v) => {
                    setType(v as any);
                    if (v === "market_outreach") setPersonalization("personalized");
                  }}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="teaser">Teaser outreach — marks the buyer tracker</SelectItem>
                      <SelectItem value="market_outreach">Market outreach — prospecting for new clients</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {type === "market_outreach" ? (
                  <div>
                    <Label className="text-xs">List <span className="text-red-500">*</span></Label>
                    <Select value={recipientSource} onValueChange={setRecipientSource}>
                      <SelectTrigger className={cn("mt-1", recipientSource === "prospects" && "border-amber-400")}>
                        <SelectValue placeholder="Which list is this going to?" />
                      </SelectTrigger>
                      <SelectContent>
                        {outreachLists.map((l) => (
                          <SelectItem key={l.id} value={l.id}>{l.name} ({l.lead_count})</SelectItem>
                        ))}
                        <SelectItem value="prospects">Prospects (CRM)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {recipientSource === "prospects"
                        ? "Pick a mass outreach list and I'll scan it and draft the campaign for you."
                        : "Selected — the campaign drafts itself from this list's context."}
                    </p>
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs">
                      Client {type === "teaser"
                        ? <span className="text-red-500">*</span>
                        : <span className="text-muted-foreground">(optional)</span>}
                    </Label>
                    <Select value={clientId} onValueChange={setClientId}>
                      <SelectTrigger className={cn("mt-1", type === "teaser" && !clientId && "border-amber-400")}>
                        <SelectValue placeholder={type === "teaser" ? "Which deal is this teaser for?" : "Select client"} />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {type === "teaser" && !clientId && (
                      <p className="text-[11px] text-amber-600 mt-1 dark:text-amber-400">
                        A teaser campaign belongs to a deal — recipients come from this client's buyer tracker, and each send marks Teaser Sent on it.
                      </p>
                    )}
                  </div>
                )}
              </div>
              {type === "market_outreach" && (
                <div className="mt-3 flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground shrink-0">Personalization</Label>
                  {(["personalized", "template"] as const).map((p) => (
                    <Button key={p} type="button" size="sm" variant={personalization === p ? "default" : "outline"}
                      className="h-7 text-xs" onClick={() => setPersonalization(p)}>
                      {p === "template" ? "One template" : <><Sparkles className="w-3 h-3 mr-1" /> AI per recipient</>}
                    </Button>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard done={!!(subject.trim() && body.trim() && (!isPersonalized || aiAngle.trim()))} title="Message">
              <div className="space-y-3">
                {isPersonalized && (
                  <div>
                    <Label className="text-xs flex items-center gap-1"><Sparkles className="w-3 h-3 text-primary-text" /> The angle — what every email should convey</Label>
                    <Textarea className="mt-1 text-sm" rows={4} value={aiAngle} onChange={(e) => setAiAngle(e.target.value)}
                      placeholder={"We're First Turn Capital, an M&A advisory firm in Oklahoma City. We work for owners on the sell side and are running two sale processes for Texas services companies right now. We offer owners a no-cost confidential market value analysis. Phone: (405) 602-2146."} />
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <p className="text-[11px] text-muted-foreground">
                        Claude combines this with each recipient's context note to write their email. The voice is fixed: short, plain-spoken, one specific detail, one ask.
                      </p>
                      <Button type="button" size="sm" variant="outline" className="shrink-0 gap-1 h-7 text-xs"
                        disabled={!aiAngle.trim() || m.suggestFallback.isPending}
                        onClick={() => {
                          m.suggestFallback.mutate(
                            { angle: aiAngle, listId: recipientSource !== "prospects" ? recipientSource : undefined },
                            {
                              onSuccess: (s) => {
                                setSubject(s.subject);
                                setBody(s.body);
                                if (!name.trim()) setName(s.name);
                                toast.success("Subject and fallback filled in — edit anything you like");
                              },
                              onError: (e: any) => toast.error(String(e.message)),
                            }
                          );
                        }}>
                        {m.suggestFallback.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        Suggest subject & fallback
                      </Button>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs">{isPersonalized ? "Fallback subject" : "Subject"}</Label>
                  <Input className="mt-1" value={subject} onChange={(e) => setSubject(e.target.value)}
                    placeholder={isPersonalized ? "your business" : "Acquisition opportunity: {{company_name}}"} />
                </div>
                <div>
                  <Label className="text-xs">{isPersonalized ? "Fallback body" : "Body"}</Label>
                  <Textarea className="mt-1 font-mono text-sm" rows={isPersonalized ? 5 : 10} value={body} onChange={(e) => setBody(e.target.value)}
                    placeholder={"Dear {{first_name}},\n\nWe are bringing a new opportunity to market..."} />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {isPersonalized
                      ? "Only used if a recipient somehow has no draft — every send normally uses their personal draft."
                      : <>Merge fields: <code>{"{{first_name}}"}</code> <code>{"{{full_name}}"}</code> <code>{"{{company_name}}"}</code> — watch them resolve in the preview.</>}
                  </p>
                </div>
                <div>
                  <Label className="text-xs">Attachments <span className="text-muted-foreground">(3 MB total)</span></Label>
                  <div className="space-y-1.5 mt-1">
                    {attachments.map((a, i) => (
                      <div key={a.objectPath} className="flex items-center gap-2 text-sm bg-muted/40 rounded px-2 py-1">
                        <Paperclip className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="flex-1 truncate">{a.filename}</span>
                        <span className="text-xs text-muted-foreground">{(a.size / 1024).toFixed(0)} KB</span>
                        <button onClick={() => setAttachments(attachments.filter((_, j) => j !== i))}>
                          <X className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                        </button>
                      </div>
                    ))}
                    <label className="inline-flex items-center gap-1.5 text-sm text-primary-text cursor-pointer hover:underline">
                      {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                      Attach file
                      <input type="file" className="hidden" onChange={handleAttach} disabled={isUploading} />
                    </label>
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard done={recipientCount > 0} title={`Recipients${recipientCount ? ` — ${recipientCount}` : ""}`}>
              <div className="space-y-3">
                {type === "market_outreach" && recipientSource !== "prospects" && (
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input type="checkbox" checked={excludeContacted} onChange={(e) => setExcludeContacted(e.target.checked)} />
                    Skip leads already contacted
                  </label>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant={recipientCount ? "outline" : "default"} onClick={buildRecipients} disabled={m.buildRecipients.isPending}>
                    {m.buildRecipients.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
                    <Users className="w-3.5 h-3.5 mr-1" />
                    {type === "market_outreach"
                      ? (recipientCount ? "Rebuild recipient list" : "Build recipient list")
                      : (recipientCount ? "Rebuild from tracker" : "Build from client's buyer tracker")}
                  </Button>
                  {report && report.noEmail.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="w-3.5 h-3.5" /> {report.noEmail.length} skipped (no email)
                    </span>
                  )}
                </div>
                {type === "market_outreach" && recipientCount === 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    {recipientSource === "prospects"
                      ? "Pulls every contact with an email from companies in the Prospects category."
                      : "Pulls leads from the selected mass outreach list — their notes prefill each person's AI context."}
                    {" "}Remove anyone who doesn't fit with the × on their row.
                  </p>
                )}

                {isPersonalized && recipients.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap border rounded-md bg-primary/5 px-3 py-2">
                    <Sparkles className="w-4 h-4 text-primary-text shrink-0" />
                    <span className="text-xs flex-1 min-w-[180px]">
                      {draftsMissing > 0
                        ? `${recipients.length - draftsMissing}/${recipients.length} drafts generated`
                        : `All drafts ready · ${draftsApproved}/${recipients.length} approved${draftsApproved < reviewRequired ? ` (need ${reviewRequired})` : ""}`}
                    </span>
                    {draftsMissing > 0 && (
                      <Button size="sm" variant="outline" onClick={() => generateAllDrafts("missing")} disabled={m.generateDrafts.isPending || !aiAngle.trim()}>
                        {m.generateDrafts.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                        {draftsMissing === recipients.length ? "Generate drafts" : `Generate ${draftsMissing} missing`}
                      </Button>
                    )}
                    {recipients.some((r) => r.custom_body) && (
                      <Button size="sm" variant="ghost" className="text-muted-foreground"
                        onClick={() => generateAllDrafts("all")}
                        disabled={m.generateDrafts.isPending || m.clearDrafts.isPending || !aiAngle.trim()}>
                        {m.clearDrafts.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                        Start over
                      </Button>
                    )}
                    <Button size="sm" onClick={() => setReviewOpen(true)} disabled={recipients.every((r) => !r.custom_body)}>
                      <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Review drafts
                    </Button>
                    {draftsMissing === 0 && draftsApproved < recipients.length && (
                      <Button size="sm" variant="outline" disabled={!campaignId || m.approveAllDrafts.isPending}
                        onClick={() => {
                          const remaining = recipients.length - draftsApproved;
                          if (!window.confirm(`Approve ${remaining} draft${remaining > 1 ? "s" : ""} without reading ${remaining > 1 ? "them" : "it"} individually?`)) return;
                          m.approveAllDrafts.mutate(campaignId!, {
                            onSuccess: (r) => toast.success(`${r.approved} draft${r.approved === 1 ? "" : "s"} approved`),
                            onError: (e: any) => toast.error(String(e.message)),
                          });
                        }}>
                        {m.approveAllDrafts.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCheck className="w-3.5 h-3.5 mr-1" />}
                        Approve all {recipients.length - draftsApproved}
                      </Button>
                    )}
                  </div>
                )}
                {recipients.length > 0 && (
                  <>
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                      <Input className="pl-8 h-8 text-sm" placeholder={`Search ${recipients.length} recipients…`}
                        value={recipientSearch} onChange={(e) => setRecipientSearch(e.target.value)} />
                    </div>
                    <div className={cn("border rounded-md divide-y overflow-y-auto", isPersonalized ? "max-h-96" : "max-h-56")}>
                      {visibleRecipients.slice(0, 200).map((r) => (
                        <div key={r.id} className="px-2.5 py-1.5 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                              <span className="font-medium">{r.name || r.email}</span>
                              {r.company_name && <span className="text-muted-foreground"> · {r.company_name}</span>}
                            </div>
                            {isPersonalized && r.custom_body && (
                              <span className={cn("text-[10px] px-1.5 py-0.5 rounded border shrink-0",
                                r.draft_status === "approved"
                                  ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800"
                                  : "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800")}>
                                {r.draft_status === "approved" ? "Approved" : "Draft"}
                              </span>
                            )}
                            <button
                              title="Remove from this campaign"
                              onClick={() => campaignId && m.removeRecipient.mutate({ campaignId, recipientId: r.id })}
                            >
                              <X className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                            </button>
                          </div>
                          {isPersonalized && (
                            <div className="mt-1 space-y-1">
                              <Input
                                className="h-7 text-xs"
                                placeholder="Context for their email — sector, size, anything you know about them…"
                                defaultValue={r.context ?? ""}
                                onBlur={(e) => {
                                  const v = e.target.value.trim();
                                  if (campaignId && v !== (r.context ?? "")) {
                                    m.updateRecipient.mutate({ campaignId, recipientId: r.id, context: v || null });
                                  }
                                }}
                              />
                              <Input
                                className="h-7 text-xs"
                                placeholder="Lead with (optional) — force the opener, e.g. 'the sale of his sister utilities company'"
                                defaultValue={r.lead_hook ?? ""}
                                onBlur={(e) => {
                                  const v = e.target.value.trim();
                                  if (campaignId && v !== (r.lead_hook ?? "")) {
                                    m.updateRecipient.mutate({ campaignId, recipientId: r.id, leadHook: v || null });
                                  }
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                      {visibleRecipients.length > 200 && (
                        <p className="text-xs text-muted-foreground px-2.5 py-1.5">…and {visibleRecipients.length - 200} more</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </SectionCard>

            <SectionCard done={sendDays.length > 0} title="Send settings">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">CC every email</Label>
                    <Input className="mt-1 h-8 text-sm" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="chad@…, caleb@…" />
                  </div>
                  <div>
                    <Label className="text-xs">BCC</Label>
                    <Input className="mt-1 h-8 text-sm" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="optional" />
                  </div>
                </div>

                {/* Schedule */}
                <div>
                  <Label className="text-xs">When to start</Label>
                  <div className="flex gap-1.5 mt-1">
                    {(["now", "scheduled"] as const).map((mode) => (
                      <button key={mode} type="button" onClick={() => setScheduleMode(mode)}
                        className={cn("flex-1 text-sm rounded-md border px-3 py-1.5 transition-colors",
                          scheduleMode === mode ? "border-primary bg-primary/5 font-medium" : "border-border hover:bg-muted/40")}>
                        {mode === "now" ? "Send when launched" : "Schedule"}
                      </button>
                    ))}
                  </div>
                  {scheduleMode === "scheduled" && (
                    <Input type="datetime-local" className="mt-2 h-8 text-sm" value={scheduledStartLocal}
                      onChange={(e) => setScheduledStartLocal(e.target.value)} />
                  )}
                </div>

                {/* Days of week */}
                <div>
                  <Label className="text-xs">Send on</Label>
                  <div className="flex gap-1 mt-1">
                    {DAY_LABELS.map((lbl, d) => (
                      <button key={d} type="button" title={["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][d]}
                        onClick={() => setSendDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort())}
                        className={cn("w-8 h-8 rounded-full text-xs font-medium transition-colors",
                          sendDays.includes(d) ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70")}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time window + timezone */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">From</Label>
                    <Select value={String(windowStart)} onValueChange={(v) => setWindowStart(Number(v))}>
                      <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{HOURS.slice(0, 24).map((h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">To</Label>
                    <Select value={String(windowEnd)} onValueChange={(v) => setWindowEnd(Number(v))}>
                      <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{HOURS.filter((h) => h > windowStart).map((h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Timezone</Label>
                    <Select value={timezone} onValueChange={setTimezone}>
                      <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{US_TIMEZONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Pacing */}
                <div>
                  <Label className="text-xs">Pacing</Label>
                  <div className="flex gap-1.5 mt-1">
                    {([["steady", "Spread evenly", "Paces sends across the window — gentler on deliverability"],
                       ["fast", "As fast as caps allow", "Sends quickly within the daily caps"]] as const).map(([val, title, desc]) => (
                      <button key={val} type="button" onClick={() => setPacing(val)} title={desc}
                        className={cn("flex-1 text-sm rounded-md border px-3 py-1.5 transition-colors",
                          pacing === val ? "border-primary bg-primary/5 font-medium" : "border-border hover:bg-muted/40")}>
                        {title}
                      </button>
                    ))}
                  </div>
                </div>

                {recipientCount > 0 && (
                  <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2.5 leading-relaxed">
                    <b>{recipientCount}</b> recipients, rotating across the firm's outreach mailboxes
                    {dailyCapacity > 0 && <> · up to <b>{dailyCapacity}/day</b>{estDays && <> · ~<b>{estDays} sending day{estDays === 1 ? "" : "s"}</b></>}</>}
                    <br />
                    {scheduleMode === "scheduled" && scheduledStartLocal ? "Starts " + scheduledStartLocal.replace("T", " ") + ` ${tzAbbrev(timezone)}, then ` : "Sends "}
                    {sendDays.length === 7 ? "every day" : sendDays.length === 5 && [1,2,3,4,5].every((d) => sendDays.includes(d)) ? "weekdays" : sendDays.map((d) => ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d]).join("/")}
                    {" "}{hourLabel(windowStart)}–{hourLabel(windowEnd)} {tzAbbrev(timezone)}, {pacing === "steady" ? "spread evenly" : "as fast as caps allow"}.
                    {anyWarming && <span className="text-orange-600 dark:text-orange-400"> Capacity grows as warming mailboxes ramp up.</span>}
                  </div>
                )}
              </div>
            </SectionCard>
          </div>

          {/* ── Right: live preview ── */}
          <div className="xl:sticky xl:top-4">
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border bg-muted/30 flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Live preview</span>
                <span className="text-[11px] text-muted-foreground ml-auto truncate">
                  as {sampleRecipient ? `${sampleName} (${sampleCompany})` : "sample recipient"}
                </span>
              </div>
              <div className="px-4 py-3 border-b border-border text-sm space-y-0.5">
                <p><span className="text-muted-foreground w-14 inline-block">From</span> {previewMailbox ? `${previewMailbox.displayName || "First Turn Capital"} <${previewMailbox.fromAddress}>` : "First Turn Capital (rotates per send)"}</p>
                <p><span className="text-muted-foreground w-14 inline-block">To</span> {sampleRecipient?.email || "buyer@example.com"}</p>
                {splitEmails(cc).length > 0 && <p><span className="text-muted-foreground w-14 inline-block">Cc</span> {splitEmails(cc).join(", ")}</p>}
                <p className="font-medium pt-1">{mergedPreview.subject}</p>
                {attachments.length > 0 && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
                    <Paperclip className="w-3 h-3" /> {attachments.map((a) => a.filename).join(" · ")}
                  </p>
                )}
              </div>
              <div
                className="px-5 py-4 text-sm max-h-[560px] overflow-y-auto bg-white text-neutral-900 [&_p]:mb-2"
                dangerouslySetInnerHTML={{ __html: mergedPreview.html }}
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 px-1">
              Exactly what recipients receive: your message, the sender's signature, the firm logo, and the compliance disclosure. The signature matches whichever outreach mailbox each send rotates to.
            </p>
          </div>
        </div>

        {/* ── Sticky action bar ── */}
        <div className="fixed bottom-0 left-0 md:left-[260px] right-0 border-t border-border bg-card/95 backdrop-blur px-6 py-3 flex items-center gap-3 z-40">
          <span className="text-xs text-muted-foreground flex-1 truncate">
            {launchProblems.length > 0 ? `To launch: ${launchProblems.join(" · ")}` : `Ready: ${recipientCount} recipients${dailyCapacity > 0 ? ` · up to ${dailyCapacity}/day` : ""}`}
          </span>
          <Button variant="outline" size="sm" onClick={testSend} disabled={!canPersist || m.testSend.isPending} className="gap-1">
            {m.testSend.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
            Send me a test
          </Button>
          <Button size="sm" onClick={launch} disabled={launchProblems.length > 0 || m.lifecycle.isPending} className="gap-1">
            <Send className="w-4 h-4" /> Launch campaign
          </Button>
        </div>

        {campaignId && (
          <DraftReviewDialog campaignId={campaignId} recipients={recipients} open={reviewOpen} onOpenChange={setReviewOpen} />
        )}
      </div>
    </AppLayout>
  );
}
